import type { Interpolation } from "@emotion/react";
import { Link, useTranslations } from "@i18n/next";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { useRequestNotifications } from "@packages/notifications";
import { useSession } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import { BellIcon, IconButton } from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import { useMemo } from "react";

const unreadStyle: Interpolation<ThemeType> = (theme) => ({
  position: "relative",
  ":after": {
    content: `""`,
    position: "absolute",
    right: 8,
    top: 8,
    width: 8,
    height: 8,
    borderRadius: "100%",
    background: theme.semanticColors.errorError,
    zIndex: 1,
  },
});

export const NotificationButton = () => {
  const t = useTranslations();
  const { status } = useSession();
  const { flags } = useMixpanel();
  const isEnabled = flags?.[HallowABFlag.WebNotifications]?.isTargeted ?? false;
  const { infiniteQuery } = useRequestNotifications({
    enabled: status === "authenticated" && isEnabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: "always",
    params: { query: { page_size: 5 } },
  });

  const hasUnread = useMemo(() => {
    return (
      infiniteQuery.data?.pages?.[0]?.some((notif) => !notif.acknowledged_at) ??
      false
    );
  }, [infiniteQuery.data]);

  if (status !== "authenticated" || !isEnabled) return null;

  return (
    <IconButton
      asChild
      data-testid={HallowDataTestId.NotificationButton}
      icon={<BellIcon.On />}
      variant="transparentPrimary"
      css={hasUnread ? unreadStyle : null}
    >
      <Link
        href="/settings/notifications"
        aria-label={t("settings_notifications_title")}
      />
    </IconButton>
  );
};
